import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import {
  BOUNDARY_MAX_VALUE_ID, BOUNDARY_MIN_VALUE_ID,
} from '~/boundary/settings/boundarySettings.helpers';
import { boundaryFillDefaultOpacity } from '~/boundary/settings/defaultBoundaryTerritoryStyles';
import { areBoundaryTerritoriesNumericValuesValid } from '~/boundary/settings/numeric/boundarySettingsNumeric.helpers';
import { useFetchBoundaryTerritories } from '~/boundary/settings/useFetchBoundaryTerritories';
import {
  applySharedOpacity, getSharedOpacity, recalculateBoundaryTerritoryColors,
} from '~/sidebar/sidebarApps/mapTools/boundary/customizeFills/boundaryFillSettings.helpers';
import { useGetBoundaryGroup } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useBoundaryTerritoryDetailsSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { boundaryTerritoryGroupsUpdateRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import type {
  BoundaryTerritory, BoundaryTerritoryGroup, BoundaryTerritoryGroupSettings,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useDataLayersBTGsSelector } from '~/store/boundaryTerritoryGroups/dataLayersTool/dataLayersBTGs.selectors';
import {
  mapSettingsDataLayersUpdateDataLayer, mapSettingsDataLayersUpdateIndividualLayerSettings,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import {
  useMapSettingsBoundaryGroupsPerDataLayersSelector, useMapSettingsDataLayerGroupsSelector,
  useMapSettingsDataLayersIndividualLayerSettingsSelector,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import type { DataLayerGroupId } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import {
  createSampleBTGForDataLayer, DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES, individualLayerSettingsToBTGSettings,
} from './dataLayersTool.helpers';

export const useDataLayersEditViaBTG = (dataLayerGroupId: DataLayerGroupId, demographic?: string | null) => {
  const dispatch = useDispatch();
  const dataLayers = useMapSettingsDataLayerGroupsSelector();
  const boundaryGroupsPerDataLayers = useMapSettingsBoundaryGroupsPerDataLayersSelector();
  const individualLayerSettings = useMapSettingsDataLayersIndividualLayerSettingsSelector();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const dataLayersBTGs = useDataLayersBTGsSelector();
  const dataLayerGroup = dataLayers.find(layer => layer.id === dataLayerGroupId);
  const boundaryGroupId = boundaryGroupsPerDataLayers[dataLayerGroupId] ?? 0;

  // #region Individual Layer Settings

  // keep in a ref so reading it doesn't force the reset effect to re-run on every unrelated individual layer settings update after initial load
  const individualLayerSettingsRef = useRef(individualLayerSettings);
  individualLayerSettingsRef.current = individualLayerSettings;

  const getIndividualSettingsFor = useCallback((demographic: Maybe<string>) => {
    const individualSettings = (demographic ? individualLayerSettingsRef.current[boundaryGroupId]?.[demographic] : null);
    return individualSettings ? individualLayerSettingsToBTGSettings(individualSettings) : null;
  }, [boundaryGroupId]);

  const [individualSettingsActive, setIndividualSettingsActive] = useState(
    () => !!getIndividualSettingsFor(demographic)?.individualSettingsActive,
  );

  // demographic changed outside, so we update the individualSettingsActive flag accordingly
  useEffect(() => {
    setIndividualSettingsActive(!!getIndividualSettingsFor(demographic)?.individualSettingsActive);
  }, [demographic, getIndividualSettingsFor]);

  // #endregion Individual Layer Settings

  // #region BTG Handling

  // kept in a ref so reading it doesn't force the reset effect to re-run on every unrelated BTG update after initial load
  const dataLayersBTGsRef = useRef(dataLayersBTGs);
  dataLayersBTGsRef.current = dataLayersBTGs;

  const getCurrentOrSampleBTG: () => BoundaryTerritoryGroup | null = useCallback(() => {
    if (!dataLayerGroup) {
      return null;
    }

    if (!demographic || !individualSettingsActive) {
      return createSampleBTGForDataLayer({ dataLayerGroup, boundaryGroupId });
    }

    const matchingBoundaryTerritoryGroup = dataLayersBTGsRef.current.find(
      btg => btg.settings.connectedEntityData?.id === dataLayerGroupId && btg.settings.demographicId === demographic
    );

    const individualSettings = getIndividualSettingsFor(demographic)?.btgSettings;

    return matchingBoundaryTerritoryGroup ? {
      ...matchingBoundaryTerritoryGroup,
      settings: {
        ...matchingBoundaryTerritoryGroup.settings,
        ...individualSettings,
        style: {
          ...matchingBoundaryTerritoryGroup.settings.style,
          ...individualSettings?.style,
        },
      } satisfies BoundaryTerritoryGroupSettings,
    } : null;
  }, [boundaryGroupId, dataLayerGroup, dataLayerGroupId, demographic, getIndividualSettingsFor, individualSettingsActive]);

  const [boundaryTerritoryGroup, setBoundaryTerritoryGroup] = useState(getCurrentOrSampleBTG);

  const defaultOpacity = boundaryFillDefaultOpacity * 100;
  const savedSharedOpacity = dataLayerGroup?.settings.style.opacity ?? defaultOpacity;
  const [sharedOpacity, setSharedOpacity] = useState(savedSharedOpacity);

  // data layer changed outside, so we reset BTG from the current store state, discarding any active changes
  useEffect(() => {
    setBoundaryTerritoryGroup(getCurrentOrSampleBTG());
    setSharedOpacity(savedSharedOpacity);
  }, [getCurrentOrSampleBTG, savedSharedOpacity]);

  // #endregion BTG Handling

  // #region Opacity

  const customRangesOpacity = boundaryTerritoryGroup
    ? getSharedOpacity(boundaryTerritoryGroup.settings.boundaryTerritories)
    : undefined;

  const handleOpacityChange = useCallback((opacity: number) => {
    if (!individualSettingsActive) {
      setSharedOpacity(opacity);
      return;
    }

    setBoundaryTerritoryGroup(current => current && {
      ...current,
      settings: {
        ...current.settings,
        boundaryTerritories: applySharedOpacity(current.settings.boundaryTerritories, opacity),
      },
    });
  }, [individualSettingsActive]);

  // #endregion Opacity

  const [isLoading, setIsLoading] = useState(false);

  const boundaryTerritoryGroupRef = useRef(boundaryTerritoryGroup);
  boundaryTerritoryGroupRef.current = boundaryTerritoryGroup;

  // fetched ranges have the server's default opacity, so the unsaved shared one is carried over
  const setFetchedBoundaryTerritoryGroup = useCallback((fetchedGroup: BoundaryTerritoryGroup) => {
    const carriedOpacity = getSharedOpacity(boundaryTerritoryGroupRef.current?.settings.boundaryTerritories ?? []);

    setBoundaryTerritoryGroup(isNullsy(carriedOpacity) ? fetchedGroup : {
      ...fetchedGroup,
      settings: {
        ...fetchedGroup.settings,
        boundaryTerritories: applySharedOpacity(fetchedGroup.settings.boundaryTerritories, carriedOpacity),
      },
    });
  }, []);

  const updateBoundaryTerritories = useFetchBoundaryTerritories(setFetchedBoundaryTerritoryGroup, setIsLoading);

  const onNumberOfRangesChange = useCallback((newNumberOfRanges: number) => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    if (!individualSettingsActive) {
      setBoundaryTerritoryGroup({
        ...boundaryTerritoryGroup,
        settings: {
          ...boundaryTerritoryGroup.settings,
          rangesCount: newNumberOfRanges,
        },
      });
      return;
    }

    const newBoundaryTerritoryGroup = {
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        rangesCount: newNumberOfRanges,
      },
    };

    updateBoundaryTerritories({
      newBoundaryTerritoryGroup,
      originalBoundaryTerritoryGroup: getCurrentOrSampleBTG() ?? newBoundaryTerritoryGroup,
      filter: null,
    });
  }, [boundaryTerritoryGroup, individualSettingsActive, updateBoundaryTerritories, getCurrentOrSampleBTG]);

  const onRecalculateRangesClick = useCallback(() => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    updateBoundaryTerritories({
      newBoundaryTerritoryGroup: boundaryTerritoryGroup,
      originalBoundaryTerritoryGroup: getCurrentOrSampleBTG() ?? boundaryTerritoryGroup,
      filter: null,
    });
  }, [boundaryTerritoryGroup, updateBoundaryTerritories, getCurrentOrSampleBTG]);

  // resets the individual style override back to the data layer's shared style, instead of the global defaults
  const handleRestoreSharedColors = useCallback(() => {
    if (!boundaryTerritoryGroup || !dataLayerGroup) {
      return;
    }

    const sharedStyle = dataLayerGroup.settings.style;

    setBoundaryTerritoryGroup({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: applySharedOpacity(
          recalculateBoundaryTerritoryColors(
            boundaryTerritoryGroup.settings.boundaryTerritories, sharedStyle.gradientColors,
          ),
          savedSharedOpacity,
        ),
        style: {
          ...boundaryTerritoryGroup.settings.style,
          gradientColors: sharedStyle.gradientColors,
        },
      },
    });
  }, [boundaryTerritoryGroup, dataLayerGroup, savedSharedOpacity]);

  // shared mode has no shared style to restore to, so the default global-default-colors behavior is used instead
  const onRestoreDefaultColors = individualSettingsActive ? handleRestoreSharedColors : undefined;

  const onBoundaryTerritoryChange = useCallback((BTs: ReadonlyArray<BoundaryTerritory>) => {
    if (boundaryTerritoryGroup) {
      setBoundaryTerritoryGroup({
        ...boundaryTerritoryGroup,
        settings: {
          ...boundaryTerritoryGroup.settings,
          boundaryTerritories: BTs,
        },
      });
    }
  }, [boundaryTerritoryGroup]);

  const onSubmit = useCallback((params: { onDone?: () => void }) => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    if (individualSettingsActive && !areBoundaryTerritoriesNumericValuesValid(boundaryTerritoryGroup.settings.boundaryTerritories)) {
      return;
    }

    const onDone = () => {
      setIsLoading(false);
      params.onDone?.();
    };

    setIsLoading(true);

    const numberOfRanges = boundaryTerritoryGroup.settings.rangesCount || DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES;

    // Update individual layer settings for this demographic
    if (demographic) {
      dispatch(mapSettingsDataLayersUpdateIndividualLayerSettings({
        boundaryGroupId,
        demographicId: demographic,
        dataLayerId: dataLayerGroupId,
        settings: {
          individualSettingsActive,
          boundarySettings: individualSettingsActive ? {
            boundaryTerritories: boundaryTerritoryGroup.settings.boundaryTerritories,
            style: {
              gradientColors: boundaryTerritoryGroup.settings.style.gradientColors,
            },
            numberOfRanges,
          } : {},
        },
      }));
    }

    if (individualSettingsActive) {
      dispatch(boundaryTerritoryGroupsUpdateRequest(boundaryTerritoryGroup, { fetchBoundaryDetails: true, onDone }));
    }
    else {
      // Update the data layer settings (shared with all demographics), unless individual settings are active for this layer
      dispatch(mapSettingsDataLayersUpdateDataLayer({
        id: dataLayerGroupId,
        settings: {
          style: {
            ...boundaryTerritoryGroup.settings.style,
            opacity: sharedOpacity,
          },
          numberOfRanges,
          disableMapInteraction: boundaryTerritoryGroup.settings.disableMapInteraction,
          hideLabels: boundaryTerritoryGroup.settings.hideLabels,
          labelStyle: boundaryTerritoryGroup.settings.labelStyle,
          labelValueConfig: boundaryTerritoryGroup.settings.labelValueConfig,
        },
      }));

      onDone();
    }
  }, [boundaryTerritoryGroup, individualSettingsActive, demographic, dispatch, boundaryGroupId, dataLayerGroupId, sharedOpacity]);

  const territoryDetails = useBoundaryTerritoryDetailsSelector();
  const numericalDataForActiveFill = territoryDetails.get(boundaryTerritoryGroup?.boundaryTerritoryGroupId ?? 0)?.numericalData;
  const rangeMin: number | typeof BOUNDARY_MIN_VALUE_ID = numericalDataForActiveFill?.totalLowest ?? BOUNDARY_MIN_VALUE_ID;
  const rangeMax: number | typeof BOUNDARY_MAX_VALUE_ID = numericalDataForActiveFill?.totalHighest ?? BOUNDARY_MAX_VALUE_ID;

  return {
    dataLayerGroup,
    boundaryTerritoryGroup,
    boundaryGroup: getBoundaryGroupById(boundaryGroupId),
    individualSettingsActive,
    rangeMin,
    rangeMax,
    opacity: individualSettingsActive && notNullsy(customRangesOpacity) ? customRangesOpacity : sharedOpacity,
    onOpacityChange: handleOpacityChange,
    onIndividualSettingsActiveChange: setIndividualSettingsActive,
    updateBoundaryTerritoryGroup: setBoundaryTerritoryGroup,
    onNumberOfRangesChange,
    onBoundaryTerritoryChange,
    onRecalculateRangesClick,
    onRestoreDefaultColors,
    isLoading,
    onSubmit,
  };
};
